import React from 'react';

interface SecurityTermsProps {
  isOpen: boolean;
  onClose: () => void;
}

const SecurityTerms: React.FC<SecurityTermsProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[201] flex items-center justify-center p-4 md:p-10">
      {/* High-End Backdrop */}
      <div 
        className="absolute inset-0 bg-slate-900/80 backdrop-blur-xl animate-fade-in"
        onClick={onClose}
      ></div>

      {/* Terms Container */}
      <div className="relative w-full max-w-5xl h-full max-h-[90vh] bg-white dark:bg-slate-950 rounded-[2.5rem] shadow-2xl border-2 border-white/20 dark:border-accent-500/10 overflow-hidden flex flex-col animate-fade-in-up">
        
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100 dark:border-slate-800 flex justify-between items-center shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-brand-900 dark:bg-accent-500 flex items-center justify-center text-white">
              <i className="fas fa-file-contract text-xl"></i>
            </div>
            <div>
              <h2 className="text-xl font-heading font-black text-brand-900 dark:text-white uppercase tracking-tight">Security Terms</h2>
              <p className="text-[10px] font-black text-accent-600 dark:text-accent-400 uppercase tracking-widest">Master Service Agreement v1.0</p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-center text-gray-400"
          >
            <i className="fas fa-times text-xl"></i>
          </button>
        </div>

        {/* Content Area */}
        <div className="flex-1 overflow-y-auto p-8 md:p-12 space-y-12 custom-scrollbar">
          
          {/* Section 1: Reseller Status */}
          <section className="space-y-4">
            <div className="flex items-center gap-3 text-accent-500 text-xs font-black uppercase tracking-widest">
              <span className="w-8 h-[1px] bg-accent-500/30"></span>
              01. Scope of Service
            </div>
            <h3 className="text-2xl font-heading font-bold text-brand-900 dark:text-white">Reseller Framework</h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
              Swarups NXT operates as an independent reseller and integrator of Artificial Intelligence SaaS products. By utilizing our services, the Client acknowledges that Swarups NXT facilitates access to technology developed by third-party vendors (hereinafter referred to as "Original Vendors").
            </p>
          </section>

          {/* Section 2: AI Disclaimers */}
          <section className="bg-amber-50 dark:bg-amber-950/10 p-8 rounded-[2rem] border border-amber-100 dark:border-amber-900/30 space-y-6">
            <div className="flex items-center gap-3 text-amber-600 dark:text-amber-500 text-xs font-black uppercase tracking-widest">
              <span className="w-8 h-[1px] bg-amber-500/30"></span>
              02. AI Autonomy Disclaimer
            </div>
            <h3 className="text-2xl font-heading font-bold text-brand-900 dark:text-white">Algorithm Behavior</h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed font-bold">
              AI "Hallucinations" & Response Quality
            </p>
            <p className="text-gray-600 dark:text-gray-400 text-sm leading-relaxed font-medium">
              The Client acknowledges that AI models can occasionally produce inaccurate information, known as "hallucinations," or responses that may not align with expectations. Swarups NXT shall <span className="text-brand-900 dark:text-white underline decoration-accent-500/50">not be held responsible</span> for the factual accuracy, sentiment, or quality of responses generated by the third-party AI bots.
            </p>
          </section>

          {/* Section 3: Liability Cap */}
          <section className="space-y-4">
            <div className="flex items-center gap-3 text-accent-500 text-xs font-black uppercase tracking-widest">
              <span className="w-8 h-[1px] bg-accent-500/30"></span>
              03. Liability Limitations
            </div>
            <h3 className="text-2xl font-heading font-bold text-brand-900 dark:text-white">Aggregate Liability Cap</h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
              To the maximum extent permitted by Indian Law, the total cumulative liability of Swarups NXT for any and all claims arising out of or related to these terms or the services, whether in contract, tort, or otherwise, shall <span className="text-brand-900 dark:text-white font-black">not exceed the amount actually paid by the Client to Swarups NXT</span> in the three (3) months immediately preceding the event giving rise to the claim.
            </p>
          </section>

          {/* Section 4: Maintenance & Uptime */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <section className="space-y-4 p-6 bg-gray-50 dark:bg-slate-900/50 rounded-2xl border border-gray-100 dark:border-slate-800">
              <div className="flex items-center gap-3 text-accent-500 text-[10px] font-black uppercase tracking-widest">
                04. Support Protocol
              </div>
              <h4 className="text-lg font-bold text-brand-900 dark:text-white tracking-tight">Maintenance SLA</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                Swarups NXT provides technical support during the following window:
              </p>
              <div className="py-2 border-y border-gray-200 dark:border-slate-700">
                <p className="text-brand-900 dark:text-white font-black text-xl">10:00 AM — 06:00 PM IST</p>
                <p className="text-[10px] font-bold text-gray-500 uppercase">Monday to Friday (Excl. Public Holidays)</p>
              </div>
            </section>

            <section className="space-y-4 p-6 bg-gray-50 dark:bg-slate-900/50 rounded-2xl border border-gray-100 dark:border-slate-800">
              <div className="flex items-center gap-3 text-accent-500 text-[10px] font-black uppercase tracking-widest">
                05. System Integrity
              </div>
              <h4 className="text-lg font-bold text-brand-900 dark:text-white tracking-tight">No Uptime Guarantee</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
                As a reseller, Swarups NXT does not control the underlying server infrastructure. Platform uptime and software stability are the <span className="font-bold">sole responsibility of the Original Vendors</span>. Swarups NXT makes no warranties regarding continuous availability.
              </p>
            </section>
          </div>

          {/* Section 5: Jurisdiction */}
          <section className="space-y-4">
             <div className="flex items-center gap-3 text-accent-500 text-xs font-black uppercase tracking-widest">
              <span className="w-8 h-[1px] bg-accent-500/30"></span>
              06. Legal Jurisdiction
            </div>
            <h3 className="text-2xl font-heading font-bold text-brand-900 dark:text-white">Governing Law</h3>
            <p className="text-gray-600 dark:text-gray-400 leading-relaxed font-medium">
              These terms shall be governed by and construed in accordance with the laws of India. Any dispute arising out of or in connection with these terms, including any question regarding its existence, validity, or termination, shall be subject to the exclusive jurisdiction of the <span className="text-brand-900 dark:text-white font-bold">Courts in Chennai, Tamil Nadu, India.</span>
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="p-6 border-t border-gray-100 dark:border-slate-800 bg-gray-50/50 dark:bg-slate-900/50 flex justify-center shrink-0">
          <button 
            onClick={onClose}
            className="px-10 py-4 bg-brand-900 dark:bg-accent-500 text-white dark:text-brand-900 rounded-2xl font-black uppercase tracking-[0.2em] text-xs shadow-xl transition-all hover:scale-105 active:scale-95"
          >
            Agree to Terms
          </button>
        </div>
      </div>
    </div>
  );
};

export default SecurityTerms;